import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { BookOpen, Calculator, Trophy } from 'lucide-react';

/**
 * Grade 4 Page
 * Displays curriculum information for Grade 4 students
 */
const Grade4 = () => {
  useEffect(() => { document.title = 'Grade 4 Curriculum — PS 155 William Paca School'; }, []);
  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        {/* Hero Section */}
        <section className="relative flex flex-col min-h-[340px] sm:min-h-[400px] md:min-h-[450px] lg:min-h-[460px] overflow-hidden bg-red-900">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://3.files.edl.io/7162/25/02/12/213232-06190934-c537-4de1-b647-97e32c0cf0b5.jpg"
              alt="Grade 4 students learning"
              className="w-full h-full object-cover"
            />
            {/* Light tint in the grade's own hue. No text sits on the photo
                any more, so it only needs a gentle top/bottom vignette. */}
            <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-red-900/50 via-red-900/15 to-red-900/40"></div>
          </div>

          {/* Lower-third band: the hero copy sits on a slim, SOLID strip
              pinned to the bottom of the hero so the photo stays visible
              above it and every line has a decidable contrast ratio. */}
          <div className="relative z-10 mt-auto w-full border-t-2 border-school-gold bg-red-900">
            <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-3.5 md:px-10 md:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
              <div className="min-w-0">
                <h1 className="text-xl font-extrabold leading-tight tracking-tight text-white md:text-2xl lg:text-3xl">
                  <span className="whitespace-nowrap">Grade 4</span>
                </h1>
                <p className="mt-0.5 text-xs font-semibold text-white md:text-sm">
                  Deep Dive into Complex Topics
                </p>
              </div>
              <div className="lg:shrink-0">
                <span className="inline-block rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-700">Ages 9-10</span>
              </div>
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-10 sm:py-11 md:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-school-blue mb-6">
                What Students Are Learning This Year
              </h2>
              <div className="w-20 h-1 bg-school-gold mx-auto mb-8 rounded-full"></div>
              <p className="text-lg sm:text-xl text-neutral-600 leading-relaxed">
                In Grade 4, students analyze texts in depth, compare ideas across <span className="font-bold text-school-gold-dark">multiple sources</span>, and write essays and explanations supported by evidence. They work with multi-digit operations and deepen their understanding of fractions.
              </p>
            </div>
          </div>
        </section>

        {/* Curriculum Areas */}
        <section className="py-16 sm:py-20 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
              {/* Reading & Writing */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-neutral-100 hover:border-school-gold/50 transition-all hover:shadow-xl">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <BookOpen className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-school-blue mb-2">
                      Reading & Writing
                    </h3>
                    <p className="text-sm text-neutral-500 font-medium">
                      NY Next Generation ELA Standards
                    </p>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-600 leading-relaxed">
                  <p>
                    Aligned to the <span className="font-semibold text-school-blue">NY Next Generation ELA Standards</span>, students:
                  </p>

                  <ul className="space-y-3 ml-4">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Analyze texts in depth</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Compare ideas across multiple sources</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Write essays and explanations supported by evidence</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Develop sophisticated argumentative and informative writing</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Engage in close reading and textual analysis</span>
                    </li>
                  </ul>

                  <div className="mt-6 pt-6 border-t border-neutral-100">
                    <p className="text-sm font-semibold text-school-blue">
                      Curriculum: <span className="text-school-gold-dark">HMH Into Reading</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Math */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-neutral-100 hover:border-school-gold/50 transition-all hover:shadow-xl">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Calculator className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-school-blue mb-2">
                      Math
                    </h3>
                    <p className="text-sm text-neutral-500 font-medium">
                      NY Next Generation Mathematics Standards
                    </p>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-600 leading-relaxed">
                  <p>
                    Aligned to the <span className="font-semibold text-school-blue">NY Next Generation Mathematics Standards</span>, students:
                  </p>

                  <ul className="space-y-3 ml-4">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Work with multi-digit multiplication and division</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Deepen understanding of fractions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Solve complex, multi-step problems</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Apply advanced problem-solving strategies</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-2 h-2 bg-school-gold rounded-full mt-2"></span>
                      <span>Use mathematical reasoning in real-world contexts</span>
                    </li>
                  </ul>

                  <div className="mt-6 pt-6 border-t border-neutral-100">
                    <p className="text-sm font-semibold text-school-blue">
                      Curriculum: <span className="text-school-gold-dark">Eureka Math</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partnerships */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100 hover:border-blue-300 transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Trophy className="w-8 h-8 text-blue-600" strokeWidth={2} />
                  <h3 className="text-2xl font-bold text-school-blue">Partnerships</h3>
                </div>
                <p className="text-lg font-semibold text-school-gold-dark">
                  NYCFC Soccer
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Curriculum & CTA */}
        <section className="py-16 sm:py-20 bg-school-blue-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Questions About Grade 4?
            </h2>
            <p className="text-lg sm:text-xl text-blue-100 mb-10 leading-relaxed max-w-2xl mx-auto">
              We're here to help! Contact us to learn more about our Grade 4 program or schedule a visit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/curriculum"
                className="px-8 py-4 bg-white text-school-blue rounded-full font-bold text-lg hover:bg-neutral-100 transition-all duration-300 hover:-translate-y-1 shadow-lg inline-flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Grades
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-school-gold text-school-blue-dark rounded-full font-bold text-lg hover:bg-yellow-500 transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Grade4;
